/** useAutoRefresh hook (UX review F3 + J1).
 *
 * - Polls `callback` every `intervalMs` while auto-refresh is on
 * - Pauses while the tab is hidden (document.visibilitychange)
 * - Re-fetches when the tab becomes visible again
 * - Re-runs immediately on manual refresh (refreshKey from RefreshContext)
 *
 * The callback is kept in a ref so identity changes (e.g. new closure per
 * render) don't restart the interval; callers re-run fetches on their own
 * deps via separate effects.
 */
import { useEffect, useRef } from "react";
import { useRefresh } from "../components/layout/RefreshContext";

export function useAutoRefresh(
  callback: () => void | Promise<void>,
  intervalMs: number | null
) {
  const { autoRefresh, refreshKey, setRefreshing } = useRefresh();
  const cbRef = useRef(callback);
  cbRef.current = callback;

  // Initial fetch + manual refresh trigger
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(cbRef.current()).finally(() => {
      if (!cancelled) setRefreshing(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Interval polling — skipped when autoRefresh is off or interval is null
  useEffect(() => {
    if (intervalMs === null || !autoRefresh) return;
    const id = window.setInterval(() => {
      if (document.hidden) return; // don't poll a background tab
      Promise.resolve(cbRef.current()).catch(() => {});
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, autoRefresh]);

  // Fetch when the tab becomes visible again (data may be stale)
  useEffect(() => {
    if (!autoRefresh) return;
    const handler = () => {
      if (!document.hidden) {
        Promise.resolve(cbRef.current()).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [autoRefresh]);
}
